import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchemaOrg, getBaseUrl } from '../../utils/seo';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Add Schema.org JSON-LD for Organization
  useSchemaOrg({
    type: 'Organization',
    data: {
      name: 'Laas Geel Environmental & Quality Consultancy',
      alternateName: 'LGEQC',
      url: getBaseUrl(),
      logo: `${getBaseUrl()}/logo.png`,
      description: 'Leading environmental consultancy tackling climate change, resource depletion, and pollution across East Africa.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Hargeisa',
        addressCountry: 'SO'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@laasgeeleqc.org',
        contactType: 'Customer Service'
      },
      sameAs: []
    }
  });

  // Add Schema.org JSON-LD for WebPage
  useSchemaOrg({
    type: 'WebPage',
    data: {
      name: 'LGEQC. Laas Geel Environmental & Quality Consultancy',
      description: 'Leading Laas Geel environmental consultancy tackling climate change, resource depletion, and pollution. Expert sustainable practices, environmental impact assessments, and community empowerment programs.',
      url: getBaseUrl(),
      inLanguage: 'en',
      isPartOf: {
        '@type': 'WebSite',
        url: getBaseUrl(),
        name: 'LGEQC'
      }
    }
  });

  // Add Schema.org JSON-LD for FAQPage (Services)
  useSchemaOrg({
    type: 'FAQPage',
    data: {
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What climate resilience and adaptation services does LGEQC provide?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'LGEQC addresses East Africa\'s increasing droughts, floods, and unpredictable rainfall patterns. We develop climate‑smart agriculture strategies, early warning systems, and community adaptation plans for the East African region.'
          }
        },
        {
          '@type': 'Question',
          name: 'How does LGEQC help with urban and industrial pollution control?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Tackling rapid urbanization challenges, we provide air quality monitoring, industrial waste management, plastic pollution reduction strategies, and water quality testing for food and medicine safety.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is LGEQC\'s approach to environmental sustainability?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our policy framework is built on sustainable practices that balance environmental protection with economic development and social equity. We prioritize evidence-based decision making, precautionary approach to environmental management, inclusive stakeholder engagement, and continuous improvement through monitoring and evaluation.'
          }
        }
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Team members data
  const teamMembers = [
    {
      id: 1,
      name: 'Mohamed Ibrahim ',
      position: 'Chief Environmental Consultant - Managing Director @ LGEQC',
      bio: 'A highly qualified professional in the environmental management discipline, with over 21 years of experience in delivering environmental and sustainability services. He is dedicated to achieving a balance between economic growth and environmental sustainability. As managing director, he has extensive experience in environmental impact assessments, marine environmental conservation, climate change challenges, and developing customised adaption strategies. ',
      image: ''
    },
    {
      id: 2,
      name: 'Adnan Ahmed',
      position: 'Senior Microbiologist - Deputy Managing Director @ LGEQC',
      bio: 'Adnan is an accomplished Senior Microbiologist with 30+ years of experience in water, food, and environmental testing. He holds a BSc in Microbiology from King Abdulaziz University and has led laboratory operations across major UAE institutions, including the Abu Dhabi Quality and Conformity Council and Al-Ain Distribution company. Highly trained in ISO 17025, microorganism identification, and laboratory quality management, Adnan is known for his strong analytical skills and ability to guide technical teams with precision and expertise.',
      image: ''
    }
  ];

  const teamMembersRow2 = [
    {
      id: 5,
      name: 'Ali Suleman Mohamad',
      position: 'Environmental Sustainability Specialist',
      bio: 'Ali is an experienced sustainability and environmental management professional with a strong background in conservation, renewable energy, disaster risk reduction, and environmental compliance. He has worked with organisations such as UNDP, Environment Agencies in the UK, rail infrastructure companies, and the Ministry of Environment, contributing to projects across the UK, GCC, and Africa. With an MSc in Environmental Sustainability Strategy and Management, Ali brings solid technical expertise in EIA, BREEAM/LEED standards, clean water initiatives, and stakeholder engagement. He is committed to advancing practical, resilient, and community-focused environmental solutions.',
      image: ''
    },
    {
      id: 3,
      name: 'Abdi Omar Ibrahim',
      position: 'Materials Engineer & Quality Control Specialist',
      bio: 'Abdi brings over 26 years of experience in materials engineering and quality control, specialising in road and infrastructure projects. With a BSc in Geology, a Diploma in Medical Laboratory Technology, and a Certificate in Gemology, he has served in senior QA/QC roles with leading organisations including Al Ain Municipality AECOM, Jacobs International, and Western Bainoona Group. His expertise includes material design, testing, and quality assurance for asphalt, concrete, and road materials.',
      image: ''
    },
    {
      id: 4,
      name: 'Awes Sheikh Muheidin Amin',
      position: 'Environmental Science Professional',
      bio: 'Awes is an environmental science professional with dual bachelor degrees from Saudi Arabia and Australia, plus a Postgraduate\'s Diploma in Low Cost Infrastructure from IHS Rotterdam. Their experiences spans consultancy, leadership in community organisation and environments health roles across Melbourne city councils. He also worked with the University of Melbourne, the local government of Mogadishu and United Nations in Somalia.',
      image: ''
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <i className={`ri-leaf-line text-2xl sm:text-3xl ${isScrolled ? 'text-teal-600' : 'text-white'}`}></i>
              <span className={`text-xl sm:text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>LGEQC</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Home</a>
              <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>About Us</a>
              <a href="#team" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Team</a>
              <a href="#services" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Services</a>
              <a href="#policy" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Policy</a>
              <a href="#contact" className="px-6 py-2 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">Contact Us</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 py-4 bg-white rounded-lg shadow-lg">
              <div className="flex flex-col space-y-3 px-4">
                <a 
                  href="#home" 
                  className="text-gray-700 hover:text-teal-600 font-medium py-2 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a 
                  href="#about" 
                  className="text-gray-700 hover:text-teal-600 font-medium py-2 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About Us
                </a>
                <a 
                  href="#team" 
                  className="text-gray-700 hover:text-teal-600 font-medium py-2 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Team
                </a>
                <a 
                  href="#services" 
                  className="text-gray-700 hover:text-teal-600 font-medium py-2 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Services
                </a>
                <a 
                  href="#policy" 
                  className="text-gray-700 hover:text-teal-600 font-medium py-2 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Policy
                </a>
                <a 
                  href="#contact" 
                  className="bg-teal-600 text-white px-6 py-3 rounded-full hover:bg-teal-700 transition-colors cursor-pointer text-center mt-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/30a2dd0efd0f304f4faeae9a6374e3d0/8b2174885eee5ccea8589d8ada4b420a.jpeg" 
            alt="East African landscape"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Laas Geel Environmental &amp; Quality Consultancy
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/95 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed">
            Leading environmental consultancy, tackling climate change, resource depletion, and controlling the quality of food, water, and medicine across the region.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#about" className="px-6 sm:px-8 py-3 sm:py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">Learn More</a>
            <a href="#contact" className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-teal-600 text-base font-semibold rounded-full hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">Get Started</a>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Who We Are</h2>
            <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16 lg:mb-20">
            <div>
              <img 
                src="https://static.readdy.ai/image/30a2dd0efd0f304f4faeae9a6374e3d0/f55bcd687fdb8b290a9cdfcc6b73cd4a.jpeg" 
                alt="East Africa map showing Somaliland"
                className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover object-top rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Committed to Environmental Excellence</h3>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                The Laas Geel Environmental &amp; Quality Consultancy is a leading environmental and quality consultancy based in East Africa, dedicated to addressing the most pressing environmental challenges of our time. With deep roots in the region and a comprehensive understanding of ecosystem conservation, we provide innovative solutions to combat climate change, promote sustainable development and reduce pollution.
              </p>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                We are dedicated to protecting public health by ensuring adherence to established legitimate standards, procedures, and regulations.
              </p>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                Through evidence-based research, strategic planning, and hands‑on experience, we help our clients navigate complex environmental regulations, reduce overhead costs and their ecological footprint, and promote a sustainable future for East Africa and beyond.
              </p>
            </div>
          </div>

          {/* Vision & Mission */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-16 lg:mb-20">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-6 sm:p-8 lg:p-10 rounded-lg">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-teal-600 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-eye-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Our Vision</h3>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                Our vision is to tackle the root causes of environmental challenges through innovative solutions in order to attain a sustainable future. We uphold the high professional and ethic standards by seamlessly integrating them with a business‑oriented mindset. Credibility and integrity are at the core of everything we do that values our vision and policies.
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 sm:p-8 lg:p-10 rounded-lg">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-emerald-600 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-compass-3-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Our Mission</h3>
              <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                Our mission is to empower our clients and customers by enhancing their capacity to achieve sustainable progress across a broad spectrum of social, economic, and environmental dimensions.
              </p>
            </div>
          </div>

          {/* Core Values */}
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Core Values</h3>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every project we undertake
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 sm:p-6">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Integrity</h4>
              <p className="text-sm text-gray-600">Honest, transparent, and ethical in all our practices</p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl sm:text-3xl text-emerald-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Technical Team</h4>
              <p className="text-sm text-gray-600">Creative solutions for complex environmental challenges</p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl sm:text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Collaboration</h4>
              <p className="text-sm text-gray-600">Working together with communities and stakeholders</p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-14 sm:w-16 h-14 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-plant-line text-2xl sm:text-3xl text-emerald-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Sustainability</h4>
              <p className="text-sm text-gray-600">Long-term solutions for lasting environmental impact</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section id="team" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Three Box Structure */}
          <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8 mb-16 lg:mb-20">
            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-teal-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-award-line text-3xl sm:text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Board of Directors</h3>
            </div>

            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-team-line text-3xl sm:text-4xl text-emerald-600"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Board of Executives</h3>
            </div>

            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-teal-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-global-line text-3xl sm:text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Chair Person</h3>
              
              {/* Three Mini Boxes under Chair Person */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 sm:mt-8 w-full">
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-lg p-4 flex flex-col items-center text-center">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-600 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-user-star-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900">Administration / Finance</h4>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 flex flex-col items-center text-center">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-lightbulb-flash-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900">Technical Team</h4>
                </div>
                
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-lg p-4 flex flex-col items-center text-center">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-600 rounded-full flex items-center justify-center mb-3">
                    <i className="ri-hand-heart-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900">IT</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Meet the Team</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our dedicated team of environmental experts bringing decades of combined experience to tackle East Africa's environmental challenges
            </p>
          </div>

          {/* First Row - 2 members */}
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-sm font-semibold text-teal-600 mb-3 sm:mb-4">{member.position}</p>
                  {member.bio && (
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {member.bio}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Second Row - 3 members */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {teamMembersRow2.map((member) => (
              <div key={member.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-sm font-semibold text-teal-600 mb-3 sm:mb-4">{member.position}</p>
                  {member.bio && (
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {member.bio}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Our Services</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Specialized environmental solutions addressing East Africa's unique ecological and developmental challenges
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-48 sm:h-56 lg:h-64">
                <img 
                  src="https://public.readdy.ai/ai/img_res/d105f29c4e9299d962a76831704aa7b5.jpg" 
                  alt="Climate change solutions"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-8">
                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-temp-hot-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">Climate Resilience &amp; Adaptation</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Addressing East Africa's increasing droughts, floods, and unpredictable rainfall patterns. We develop climate‑smart agriculture strategies, early warning systems, and community adaptation plans for the East African region.
                </p>
                <a href="#contact" className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-48 sm:h-56 lg:h-64">
                <img 
                  src="https://static.readdy.ai/image/30a2dd0efd0f304f4faeae9a6374e3d0/2eb8e5b752059961cad0b17291cb8d96.png" 
                  alt="Pollution control"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-8">
                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-flask-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">Urban &amp; Industrial Pollution Control</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Tackling rapid urbanization challenges, we provide air quality monitoring, industrial waste management, plastic pollution reduction strategies, and water quality testing for food and medicine safety.
                </p>
                <a href="#contact" className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Section */}
      <section id="policy" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Our Policy</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Committed to sustainable practices that create lasting positive environmental impact
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Sustainable%20environmental%20practices%20illustration%20showing%20green%20technology%2C%20renewable%20energy%2C%20conservation%20efforts%2C%20and%20ecosystem%20protection%2C%20professional%20infographic%20style%20with%20clean%20simple%20background%2C%20modern%20design%20with%20natural%20green%20and%20earth%20tones&width=800&height=600&seq=policy-main-001&orientation=landscape" 
                alt="Sustainable practices"
                className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover object-top rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Sustainable Practices at Our Core</h3>
              <p className="text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                Our policy framework is built on the foundation of sustainable practices that balance environmental protection with economic development and social equity. This policy sets forth Laas Geel Environmental Consultant's commitment in delivering professional &amp; environmental consulting services that promote sustainability, legal compliance and environmental stewardship. We believe that true sustainability requires a holistic approach that considers the interconnected nature of ecosystems, communities, and economies.
              </p>
              <p className="text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                Every project we undertake is guided by principles of environmental stewardship, scientific rigor, and community participation. We prioritize solutions that not only address immediate environmental challenges but also build long‑term resilience and adaptive capacity.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-5 sm:w-6 h-5 sm:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xs sm:text-sm text-teal-600"></i>
                  </div>
                  <p className="text-sm text-gray-700">Evidence‑based decision making using the latest scientific research and data</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 sm:w-6 h-5 sm:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xs sm:text-sm text-teal-600"></i>
                  </div>
                  <p className="text-sm text-gray-700">Precautionary approach to environmental management and risk assessment</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 sm:w-6 h-5 sm:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xs sm:text-sm text-teal-600"></i>
                  </div>
                  <p className="text-sm text-gray-700">Inclusive stakeholder engagement and community‑centered solutions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 sm:w-6 h-5 sm:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xs sm:text-sm text-teal-600"></i>
                  </div>
                  <p className="text-sm text-gray-700">Continuous improvement through monitoring, evaluation, and adaptive management</p>
                </div>
              </div>
            </div>
          </div>

          {/* Objectives Section */}
          <div className="mb-12 sm:mb-16">
            <div className="text-center mb-8 sm:mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Objectives</h3>
              <p className="text-base text-gray-600 max-w-3xl mx-auto">
                Strategic goals that drive our environmental initiatives and guide our commitment to sustainability
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-lg p-6 sm:p-8 lg:p-10">
              <div className="prose prose-lg max-w-none">
                <ul className="space-y-3">
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Environmental and Social Impacts Assessment
</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Strategic Environmental Assessments</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Climate Change Risk Assessments</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Sustainability and Development Planning</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Circular Economy and Resource Efficiency Solutions</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Environmental IS0 14000 and Quality ISO 17025 Audits</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Remediation and Rehabilitation of Contaminated Sites</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Industrial Planning </span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Environmental Modelling </span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Stormwater and Drainage Management </span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Biodiversity Conservation and Compensation Programs
</span>
                  </li>
                  <li className="text-base text-gray-700 flex items-start">
                    <i className="ri-checkbox-circle-line text-teal-600 text-lg sm:text-xl mt-0.5 mr-3 flex-shrink-0"></i>
                    <span>Disaster Management and Aftermath Reporting</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Policy Pillars */}
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-6 sm:p-8 rounded-lg">
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-earth-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Climate Action</h4>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We prioritize climate change mitigation and adaptation strategies that reduce greenhouse gas emissions, enhance carbon sequestration, and build resilience to climate impacts.
              </p>
              <ul className="space-y-2">
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Carbon neutrality targets</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Renewable energy promotion</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Climate risk assessments</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 sm:p-8 rounded-lg">
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-emerald-600 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-water-flash-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Resource Conservation</h4>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Our approach emphasizes efficient resource use, circular economy principles, and the protection of natural capital to prevent depletion and ensure availability for future generations.
              </p>
              <ul className="space-y-2">
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                  <span>Water stewardship programs</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                  <span>Sustainable land management</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                  <span>Biodiversity protection</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-6 sm:p-8 rounded-lg">
              <div className="w-12 sm:w-14 h-12 sm:h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-leaf-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Pollution Prevention</h4>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                We advocate for pollution prevention at the source, implementing best practices for waste management, emission control, and environmental remediation across all sectors.
              </p>
              <ul className="space-y-2">
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Zero waste initiatives</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Clean production methods</span>
                </li>
                <li className="text-sm text-gray-600 flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                  <span>Environmental monitoring</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Get In Touch</h2>
            <p className="text-base text-gray-600">
              Ready to make a positive environmental impact? Contact us today
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 lg:p-12">
            <form 
              id="contact-form"
              data-readdy-form
              className="space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                const originalText = submitButton.textContent;
                
                // Validate textarea length
                const message = formData.get('message') as string;
                if (message && message.length > 500) {
                  alert('Message must be 500 characters or less');
                  return;
                }
                
                try {
                  submitButton.disabled = true;
                  submitButton.textContent = 'Sending...';
                  
                  const response = await fetch('https://readdy.ai/api/form/d6h8jcjlm9ihjki3ek9g', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  });
                  
                  if (response.ok) {
                    submitButton.textContent = 'Message Sent!';
                    submitButton.classList.remove('bg-teal-600', 'hover:bg-teal-700');
                    submitButton.classList.add('bg-green-600');
                    form.reset();
                    setTimeout(() => {
                      submitButton.textContent = originalText;
                      submitButton.classList.remove('bg-green-600');
                      submitButton.classList.add('bg-teal-600', 'hover:bg-teal-700');
                      submitButton.disabled = false;
                    }, 3000);
                  } else {
                    throw new Error('Failed to send');
                  }
                } catch (error) {
                  submitButton.textContent = 'Failed to send. Please try again.';
                  submitButton.classList.remove('bg-teal-600', 'hover:bg-teal-700');
                  submitButton.classList.add('bg-red-600');
                  setTimeout(() => {
                    submitButton.textContent = originalText;
                    submitButton.classList.remove('bg-red-600');
                    submitButton.classList.add('bg-teal-600', 'hover:bg-teal-700');
                    submitButton.disabled = false;
                  }, 3000);
                }
              }}
            >
              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input 
                    type="text"
                    name="fullName"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input 
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
                  <input 
                    type="text"
                    name="organization"
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your Organization"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Interest</label>
                  <select 
                    name="serviceInterest"
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  >
                    <option>Climate Change Mitigation</option>
                    <option>Resource Management</option>
                    <option>Pollution Prevention</option>
                    <option>Environmental Impact Assessment</option>
                    <option>Community Empowerment</option>
                    <option>Policy Advisory</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea 
                  name="message"
                  rows={6}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Tell us about your project or inquiry... (max 500 characters)"
                ></textarea>
              </div>
              <button 
                type="submit"
                className="w-full px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                Send Message
              </button>
            </form>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-8 sm:mt-12">
            <div className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-md">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-lg sm:text-xl text-teal-600"></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900 mb-2">Email Us</h4>
              <a href="mailto:contact@laasgeeleqc.org" className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer">contact@laasgeeleqc.org</a>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-md">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-lg sm:text-xl text-teal-600"></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900 mb-2">Visit Us</h4>
              <p className="text-sm text-gray-600">Hargeisa, Somaliland</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <i className="ri-leaf-line text-2xl sm:text-3xl text-teal-400"></i>
                <span className="text-lg sm:text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>LGEQC</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Leading environmental consultancy tackling climate change, resource depletion, and pollution across East Africa.
              </p>
            </div>
            <div>
              <h4 className="text-base font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#services" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Services</a></li>
                <li><a href="#policy" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Policy</a></li>
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-gray-400">Climate Change Mitigation</span></li>
                <li><span className="text-sm text-gray-400">Resource Management</span></li>
                <li><span className="text-sm text-gray-400">Pollution Prevention</span></li>
                <li><span className="text-sm text-gray-400">Policy Advisory</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-sm text-gray-400 mb-4 sm:mb-0">
              © 2025 LGEQC. All rights reserved.
            </p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}